import { Button } from '@fluentui/react-button';
import { makeStyles } from '@fluentui/react-components';
import { ErrorCircleRegular } from '@fluentui/react-icons';
import { Tooltip } from '@fluentui/react-tooltip';
import React, { useCallback, useState } from 'react';
import { COPY } from '../../../assets/strings';
import { useChat, useFile } from '../../../libs/hooks';
import { useAppSelector } from '../../../redux/app/hooks';
import { RootState, store } from '../../../redux/app/store';
import { FeatureKeys } from '../../../redux/features/app/AppState';
import { Breakpoints } from '../../../styles';
import { ArrowDownload16, Edit, Share20 } from '../../shared/BundledIcons';
import { InvitationCreateDialog } from '../invitation-dialog/InvitationCreateDialog';
import { DeleteChatDialog } from './dialogs/DeleteChatDialog';
import { Roles } from '../../../libs/auth/AuthHelper';

const useClasses = makeStyles({
    root: {
        display: 'contents',
        ...Breakpoints.small({
            display: 'none',
        }),
    },
});

interface IListItemActionsProps {
    chatId: string;
    onEditTitleClick: () => void;
}

export const ListItemActions: React.FC<IListItemActionsProps> = ({ chatId, onEditTitleClick }) => {
    const classes = useClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const { conversations } = useAppSelector((state: RootState) => state.conversations);
    const isEnabled = false;

    const chat = useChat();
    const { downloadFile } = useFile();
    const [isGettingInvitationId, setIsGettingInvitationId] = useState(false);
    const { activeUserInfo } = store.getState().app;
    const isAdmin = activeUserInfo?.roles.includes(Roles.Admin)?? false;

    const onDownloadBotClick = useCallback(() => {
        // TODO: [Issue #47] Add a loading indicator
        void chat.downloadBot(chatId).then((content) => {
            downloadFile(
                `chat-history-${chatId}-${new Date().toISOString()}.json`,
                JSON.stringify(content),
                'text/json',
            );
        });
    }, [chat, chatId, downloadFile]);

    return (
        <div className={classes.root}>
            {conversations[chatId].disabled ? (
                <Tooltip content={COPY.CHAT_DELETED_MESSAGE()} relationship="label">
                    <Button
                        icon={<ErrorCircleRegular />}
                        appearance="transparent"
                        aria-label="Alert: Chat has been deleted by another user."
                    />
                </Tooltip>
            ) : (
                <>
                    <Tooltip content={'Chat-Name bearbeiten'} relationship="label">
                        <Button
                            icon={<Edit />}
                            appearance="transparent"
                            aria-label="Chat-Name bearbeiten"
                            onClick={onEditTitleClick}
                            data-testid="editChatTitleButtonSimplified"
                        />
                    </Tooltip>
                        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition*/}
                    {isAdmin && isEnabled && (
                            <>
                                <Tooltip content={'Chatsitzung herunterladen'} relationship="label">
                                    <Button
                                        disabled={!features[FeatureKeys.BotAsDocs].enabled}
                                        icon={<ArrowDownload16 />}
                                        appearance="transparent"
                                        aria-label="Chatsitzung herunterladen"
                                        onClick={onDownloadBotClick}
                                    />
                                </Tooltip>
                                <Tooltip content={'Live-Chat-Code freigeben'} relationship="label">
                                    <Button
                                        disabled={!features[FeatureKeys.MultiUserChat].enabled}
                                        icon={<Share20 />}
                                        appearance="transparent"
                                        aria-label="Live-Chat-Code freigeben"
                                        onClick={() => {
                                            setIsGettingInvitationId(true);
                                        }}
                                    />
                                </Tooltip>
                            </>
                        )}
                    <DeleteChatDialog chatId={chatId} />
                    {isGettingInvitationId && (
                        <InvitationCreateDialog
                            onCancel={() => {
                                setIsGettingInvitationId(false);
                            }}
                            chatId={chatId}
                        />
                    )}
                </>
            )}
        </div>
    );
};
